// The Jitto Answer dashboard map (ANS-545): every asset-class dashboard the app
// can open is in it, each value is an embedded uuid or null (UNSET), and the
// embed resolves through each environment's own ThoughtSpot ids.
import map from './jittoDashboards.json';
import dashboardIds from './jittoDashboardIds.json';
import {
  embeddedIdFor,
  jittoEmbedIdFor,
  jittoDashboardList,
  mountJittoDashboard,
  jittoDashboardIdFor,
} from '../features/dashboards/jittoEmbed';

// The real SDK is an ES module Jest would load; the adapter is tested with it mocked
// (babel-jest hoists this above the imports).
jest.mock('@jitto/analyze-embed', () => ({ mountJittoDashboard: jest.fn() }));

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
// `test` is jest's own fixture env (its missing dashboards are `mock-id`), so
// the coverage check runs over the environments people use.
const ENVS = ['development', 'dev', 'npd', 'prd', 'jld'];
const PM_OVERVIEW = '5a9861f1-dc6c-4003-934f-f16b324db989';
const SUMMARY = '2dedd749-d080-4269-9bc0-c05c0a8af2cb';

const jittoDashboards = () => JSON.parse(JSON.stringify(map));
const envConfig = (env) => require(`./${env}`).default;

test('every value is an embedded uuid or UNSET (null)', () => {
  for (const [assetClass, dashboards] of Object.entries(map)) {
    for (const [key, id] of Object.entries(dashboards)) {
      expect([assetClass, key, id === null || UUID.test(id)]).toEqual([
        assetClass,
        key,
        true,
      ]);
    }
  }
});

test('no embedded uuid is used twice', () => {
  const ids = Object.values(map)
    .flatMap((d) => Object.values(d))
    .filter(Boolean);
  expect(new Set(ids).size).toBe(ids.length);
});

// The dashboards an environment opens: every id that is not `na`.
const opened = (dashboards) =>
  Object.entries(dashboards).flatMap(([assetClass, { ids }]) =>
    Object.entries(ids)
      .filter(([, vizId]) => vizId && vizId !== 'na')
      .map(([key, vizId]) => [assetClass, key, vizId]),
  );

test.each(ENVS)(
  '%s: every dashboard the app opens is in the map, and every embed it defines is opened',
  (env) => {
    const { dashboards } = envConfig(env);
    const open = opened(dashboards).map(([a, k]) => `${a}.${k}`);
    // Only the asset classes this environment has: FX Swap is not in every one.
    const inMap = Object.entries(map)
      .filter(([assetClass]) => assetClass in dashboards)
      .flatMap(([assetClass, d]) => Object.keys(d).map((key) => `${assetClass}.${key}`));
    const embedded = Object.entries(map)
      .filter(([assetClass]) => assetClass in dashboards)
      .flatMap(([assetClass, d]) =>
        Object.entries(d)
          .filter(([, id]) => id)
          .map(([key]) => `${assetClass}.${key}`),
      );
    expect(open.filter((k) => !inMap.includes(k))).toEqual([]);
    expect(embedded.filter((k) => !open.includes(k))).toEqual([]);
  },
);

test.each(ENVS)('%s: each dashboard resolves to its own embed, or to none', (env) => {
  const { dashboards } = envConfig(env);
  for (const [assetClass, key, vizId] of opened(dashboards)) {
    expect([assetClass, key, embeddedIdFor(vizId, dashboards)]).toEqual([
      assetClass,
      key,
      (map[assetClass] || {})[key] || null,
    ]);
  }
  expect(embeddedIdFor(dashboards.fixedincome.ids.pmoverview, dashboards)).toBe(PM_OVERVIEW);
  expect(embeddedIdFor(dashboards.fixedincome.ids.summary, dashboards)).toBe(SUMMARY);
});

test('jld offers Outliers where Jitto Answer has one, and FX Swap Trade Details only', () => {
  const { dashboards } = envConfig('jld');
  for (const [assetClass, { ids }] of Object.entries(dashboards)) {
    expect([assetClass, ids.outliers !== 'na']).toEqual([
      assetClass,
      Boolean((map[assetClass] || {}).outliers),
    ]);
  }
  expect(
    Object.entries(dashboards.fxswap.ids)
      .filter(([, id]) => id !== 'na')
      .map(([key]) => key),
  ).toEqual(['tradedetails']);
});

// CUS-178: NPD routes the 15 dashboards production's switch-over routes, FX Swap Trade
// Details on its embedded uuid in the shared map, as in production.
test('npd routes all 15 dashboards, FX Swap Trade Details on its shared-map id', () => {
  const { dashboards } = envConfig('npd');
  expect(opened(dashboards)).toHaveLength(15);
  expect(dashboards.fxswap.ids.tradedetails).toBe(map.fxswap.tradedetails);
});

test('jld lists every dashboard it embeds, once, by its route id', () => {
  const { dashboards } = envConfig('jld');
  const list = jittoDashboardList(dashboards);
  const embedded = opened(dashboards).filter(([, , vizId]) => embeddedIdFor(vizId, dashboards));
  expect(list.map((d) => d.id).sort()).toEqual(embedded.map(([, , vizId]) => vizId).sort());
  expect(list).toContainEqual({
    id: dashboards.fx.ids.outliers,
    name: 'FX Spot — Outliers',
    assetClass: 'FX Spot',
  });
  expect(list[0].name).toBe('Fixed Income — Summary');
});

test('a dashboard with no embed is not listed', () => {
  const { dashboards } = envConfig('jld');
  const map = { ...jittoDashboards(), fx: { summary: null } };
  const fx = jittoDashboardList(dashboards, map).filter((d) => d.assetClass === 'FX Spot');
  expect(fx).toEqual([]);
});

test('the flag decides, and prd keeps ThoughtSpot until UAT', () => {
  const prd = envConfig('prd');
  expect(
    jittoEmbedIdFor(prd.dashboards.fixedincome.ids.pmoverview, prd),
  ).toBeNull();
  const on = { ...prd, jittoEmbedActiveFlag: true };
  expect(jittoEmbedIdFor(prd.dashboards.fixedincome.ids.pmoverview, on)).toBe(
    PM_OVERVIEW,
  );
});

const SUMMARY_DASHBOARD = '158ad6f3-e740-4fa8-9909-d49751427dd5';
// Jitto's dashboard ids are the bundles' dashboard_uuid beside each embedded_uuid
// (jitto-analyze deploy/superset/dashboards/*/bundle.yaml). PM Overview's bundle is
// verel-fi, named for the old Verel Jitto Analyze it was recovered from.
const PM_OVERVIEW_DASHBOARD = 'c7480330-70c2-46f4-8070-cf46df686d2d';

test('every embedded dashboard maps to one Jitto dashboard, and none twice', () => {
  const values = Object.values(dashboardIds);
  expect(values.every((v) => UUID.test(v))).toBe(true);
  expect(new Set(values).size).toBe(values.length);
  const embedded = [...new Set(Object.values(map).flatMap((d) => Object.values(d)).filter(Boolean))];
  expect(Object.keys(dashboardIds).sort()).toEqual(embedded.sort());
  expect(jittoDashboardIdFor(PM_OVERVIEW)).toBe(PM_OVERVIEW_DASHBOARD);
});

test('each environment that embeds names its Jitto origin, https except on this machine', () => {
  for (const env of ENVS) {
    const cfg = envConfig(env);
    if (!cfg.jittoEmbedActiveFlag || env === 'prd') continue;
    expect(cfg.jittoEmbedOrigin).toMatch(env === 'development' ? /^https?:\/\// : /^https:\/\/[^/]+$/);
  }
});

test('mount hands the SDK the Jitto dashboard id, the origin and a fetchToken', async () => {
  const handle = { unmount: jest.fn() };
  const mountFn = jest.fn(() => handle);
  const requestToken = jest.fn(async (id) => ({
    token: 'jeh_x', dashboardId: id, embedUrl: 'https://app.example/embed', expiresIn: 300,
  }));
  const onFailed = jest.fn();
  const mountPoint = {};
  const got = mountJittoDashboard({
    embedId: SUMMARY, mountPoint, requestToken, embedOrigin: 'https://app.example', onFailed, mountFn,
  });

  expect(got).toBe(handle);
  const [point, opts] = mountFn.mock.calls[0];
  expect(point).toBe(mountPoint);
  expect(opts.dashboardId).toBe(SUMMARY_DASHBOARD);
  expect(opts.embedOrigin).toBe('https://app.example');
  expect(opts.onFailed).toBe(onFailed);
  expect(await opts.fetchToken(SUMMARY_DASHBOARD)).toEqual({
    token: 'jeh_x', dashboardId: SUMMARY_DASHBOARD, embedUrl: 'https://app.example/embed', expiresIn: 300,
  });
  expect(requestToken).toHaveBeenCalledWith(SUMMARY_DASHBOARD);
});

test("mount hands the SDK the first render's filters, unchanged", () => {
  const mountFn = jest.fn(() => ({}));
  const filters = Object.freeze({ Ccy: 'SGD' });
  mountJittoDashboard({
    embedId: SUMMARY, mountPoint: {}, requestToken: jest.fn(), embedOrigin: 'https://app.example', filters, mountFn,
  });
  expect(mountFn.mock.calls[0][1].filters).toBe(filters);
});

test('mount hands the SDK onSizeChanged, so the page can grow the frame to fit', () => {
  const mountFn = jest.fn(() => ({}));
  const onSizeChanged = jest.fn();
  mountJittoDashboard({
    embedId: SUMMARY, mountPoint: {}, requestToken: async () => ({}),
    embedOrigin: 'https://app.example', onSizeChanged, mountFn,
  });
  expect(mountFn.mock.calls[0][1].onSizeChanged).toBe(onSizeChanged);
});

test('a refusal reaches the SDK as its status and Jitto code', async () => {
  const mountFn = jest.fn(() => ({}));
  const refusal = { status: 403, clone: () => ({ json: async () => ({ code: 'no_group' }) }) };
  mountJittoDashboard({
    embedId: SUMMARY, mountPoint: {}, requestToken: async () => { throw refusal; },
    embedOrigin: 'https://app.example', mountFn,
  });
  const err = await mountFn.mock.calls[0][1].fetchToken(SUMMARY_DASHBOARD).catch((e) => e);
  expect(err.status).toBe(403);
  expect(err.code).toBe('no_group');
});

test('a network failure reaches the SDK without a status', async () => {
  const mountFn = jest.fn(() => ({}));
  mountJittoDashboard({
    embedId: SUMMARY, mountPoint: {}, requestToken: async () => { throw new TypeError('Failed to fetch'); },
    embedOrigin: 'https://app.example', mountFn,
  });
  const err = await mountFn.mock.calls[0][1].fetchToken(SUMMARY_DASHBOARD).catch((e) => e);
  expect(err.status).toBeUndefined();
});

test('an embedded dashboard with no Jitto dashboard is refused before the SDK runs', () => {
  const mountFn = jest.fn();
  expect(jittoDashboardIdFor(PM_OVERVIEW, {})).toBeNull();
  expect(() =>
    mountJittoDashboard({ embedId: PM_OVERVIEW, mountPoint: {}, requestToken: jest.fn(), mountFn, ids: {} }),
  ).toThrow(PM_OVERVIEW);
  expect(mountFn).not.toHaveBeenCalled();
});
